import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { notificationDanger } from '@/components/ui/Notification/Notification'
import { getNetworkDownloadSpeedInMBPS } from '@/helpers/getNetworkSpeed'
import { logger, LOGGER_LEVEL } from '@/network/common/logger'
import { useNetworkGateaway, MAX_TIME_BEFORE_RETRY } from '@/providers/NetworkGateway.provider'

export enum ERROR_REASON {
  CLIENT_SIDE = 'CLIENT_SIDE',
  SERVER_SIDE = 'SERVER_SIDE',
}

export enum NetworSpeed {
  SLOW = 'SLOW',
  FAST = 'FAST',
  NO_INTERNET = 'NO_INTERNET',
  UNKNOWN = 'UNKNOWN',
}

export const checkInternetConnection = async () => {
  try {
    const networkSpeedInMbPerSecond = await getNetworkDownloadSpeedInMBPS()
    if (parseInt(networkSpeedInMbPerSecond) > 0 && parseInt(networkSpeedInMbPerSecond) < 100) {
      return { networkSpeed: NetworSpeed.SLOW, networkSpeedInMbPerSecond }
    } else if (parseInt(networkSpeedInMbPerSecond) >= 100) {
      return { networkSpeed: NetworSpeed.FAST, networkSpeedInMbPerSecond }
    } else {
      return { networkSpeed: NetworSpeed.NO_INTERNET, networkSpeedInMbPerSecond }
    }
  } catch (error) {
    return { networkSpeed: NetworSpeed.UNKNOWN, networkSpeedInMbPerSecond: 0 }
  }
}

export const ErrorNetworkHandler = () => {
  const { hasEncounteredNetworkError, nextTimeBeforeRetry, hasEncounteredInternalError } = useNetworkGateaway()
  const [hasWSBeenDisplayed, setHasWSBeenDisplayed] = useState(false)
  const [hasNetowrkBeenDisplayed, setHasNetworkWSBeenDisplayed] = useState(false)

  const { t } = useTranslation()

  useEffect(() => {
    if (nextTimeBeforeRetry === MAX_TIME_BEFORE_RETRY) {
      checkInternetConnection().then(({ networkSpeed, networkSpeedInMbPerSecond }) => {
        if (networkSpeed === NetworSpeed.FAST) {
          logger({
            level: LOGGER_LEVEL.ERROR,
            message: "Couldn't reconnect to the WS while having a fast network speed",
            meta: { networkSpeedInMbPerSecond },
          })
          if (!hasWSBeenDisplayed) notificationDanger(t('wsCantReconnectFastNetwork'))
        } else if (networkSpeed === NetworSpeed.SLOW) {
          logger({
            level: LOGGER_LEVEL.ERROR,
            message: "Couldn't reconnect to the WS while having a slow network speed",
            meta: { networkSpeedInMbPerSecond },
          })
          if (!hasWSBeenDisplayed) notificationDanger(t('wsCantReconnectSlowNetwork'))
        } else if (networkSpeed === NetworSpeed.NO_INTERNET) {
          logger({
            level: LOGGER_LEVEL.ERROR,
            message: "Couldn't reconnect to the WS while having no internet connection",
            meta: { networkSpeedInMbPerSecond },
          })
          if (!hasWSBeenDisplayed) notificationDanger(t('wsCantReconnectNoInternet'))
        } else if (networkSpeed === NetworSpeed.UNKNOWN) {
          logger({
            level: LOGGER_LEVEL.ERROR,
            message: "Couldn't reconnect to the WS while having an unknown network speed",
            meta: { networkSpeedInMbPerSecond },
          })
          if (!hasWSBeenDisplayed) notificationDanger(t('wsCantReconnectUnknownNetwork'))
        }
        setHasWSBeenDisplayed(true)
      })
    }
  }, [nextTimeBeforeRetry])

  useEffect(() => {
    if (hasEncounteredNetworkError) {
      checkInternetConnection().then(({ networkSpeed, networkSpeedInMbPerSecond }) => {
        if (networkSpeed === NetworSpeed.FAST) {
          logger({
            level: LOGGER_LEVEL.ERROR,
            message: "Couldn't reach the backend while having a fast network speed",
            meta: { networkSpeedInMbPerSecond },
          })
          if (!hasNetowrkBeenDisplayed) notificationDanger(t('cantReachBackendFastNetwork'))
        } else if (networkSpeed === NetworSpeed.SLOW) {
          logger({
            level: LOGGER_LEVEL.ERROR,
            message: "Couldn't reach the backend while having a slow network speed",
            meta: { networkSpeedInMbPerSecond },
          })
          if (!hasNetowrkBeenDisplayed) notificationDanger(t('cantReachBackendSlowNetwork'))
        } else if (networkSpeed === NetworSpeed.NO_INTERNET) {
          logger({
            level: LOGGER_LEVEL.ERROR,
            message: "Couldn't reach the backend while having no internet connection",
            meta: { networkSpeedInMbPerSecond },
          })
          if (!hasNetowrkBeenDisplayed) notificationDanger(t('cantReachBackendNoInternet'))
        } else if (networkSpeed === NetworSpeed.UNKNOWN) {
          logger({
            level: LOGGER_LEVEL.ERROR,
            message: "Couldn't reach the backend while having an unknown network speed",
            meta: { networkSpeedInMbPerSecond },
          })
          if (!hasNetowrkBeenDisplayed) notificationDanger(t('cantReachBackendUnknownNetwork'))
        }

        setHasNetworkWSBeenDisplayed(true)
      })
    }
  }, [hasEncounteredNetworkError])

  useEffect(() => {
    if (hasEncounteredInternalError) {
      notificationDanger(t('internalServerError'))
    }
  }, [hasEncounteredInternalError])

  return null
}
